import { RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { WebhookDelivery } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { DeliveryStatusBadge } from './webhook-badges';

export interface DeliveryColumnsOptions {
  redeliveringId: string | null;
  onRedeliver: (delivery: WebhookDelivery) => void;
}

/** Column model of an endpoint's delivery log; `meta` ties columns to its sort and filters. */
export function useDeliveryColumns({ redeliveringId, onRedeliver }: DeliveryColumnsOptions) {
  const { t } = useTranslation('webhooks');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<WebhookDelivery>>>(
    () => [
      {
        id: 'event',
        header: t('deliveries.event'),
        cell: ({ row }) => (
          <code className="font-mono text-[13px] font-medium">{row.original.event}</code>
        ),
        meta: { label: t('deliveries.event'), filterKey: 'event', lockVisibility: true },
      },
      {
        id: 'status',
        header: t('deliveries.status'),
        cell: ({ row }) => <DeliveryStatusBadge status={row.original.status} />,
        meta: { label: t('deliveries.status'), filterKey: 'status' },
        size: 110,
      },
      {
        id: 'attempt',
        header: t('deliveries.attempt'),
        cell: ({ row }) => <span className="tabular-nums">{row.original.attempt}</span>,
        meta: { label: t('deliveries.attempt'), align: 'end' },
        size: 80,
      },
      {
        id: 'response',
        header: t('deliveries.response'),
        cell: ({ row }) => {
          const { responseStatus, responseBodyExcerpt } = row.original;
          if (responseStatus === null && !responseBodyExcerpt) {
            return <span className="text-muted-foreground">{t('deliveries.noResponse')}</span>;
          }
          return (
            <span className="flex min-w-0 flex-col gap-0.5">
              {responseStatus !== null ? (
                <span
                  className={cn(
                    'font-mono text-[13px] tabular-nums',
                    responseStatus >= 400 && 'text-destructive',
                  )}
                >
                  {responseStatus}
                </span>
              ) : null}
              {responseBodyExcerpt ? (
                <span
                  className="text-muted-foreground max-w-64 truncate text-xs"
                  title={responseBodyExcerpt}
                >
                  {responseBodyExcerpt}
                </span>
              ) : null}
            </span>
          );
        },
        meta: { label: t('deliveries.response') },
      },
      {
        id: 'when',
        header: t('deliveries.when'),
        cell: ({ row }) => {
          const { createdAt, nextAttemptAt, deliveredAt, status } = row.original;
          return (
            <span className="flex flex-col gap-0.5 text-sm">
              <span title={format.utc(createdAt)}>{format.relative(createdAt)}</span>
              {status === 'retrying' && nextAttemptAt ? (
                <span className="text-muted-foreground text-xs" title={format.utc(nextAttemptAt)}>
                  {t('deliveries.nextAttempt', { when: format.relative(nextAttemptAt) })}
                </span>
              ) : null}
              {deliveredAt ? (
                <span className="text-muted-foreground text-xs" title={format.utc(deliveredAt)}>
                  {t('deliveries.deliveredAt', { when: format.relative(deliveredAt) })}
                </span>
              ) : null}
            </span>
          );
        },
        meta: { label: t('deliveries.when'), sortField: 'createdAt', filterKey: 'createdAt' },
        size: 150,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('columns.actions')}</span>,
        meta: { label: t('columns.actions'), align: 'end' },
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            disabled={redeliveringId === row.original.id}
            aria-label={t('deliveries.redeliverOne', { event: row.original.event })}
            onClick={(e) => {
              e.stopPropagation();
              onRedeliver(row.original);
            }}
          >
            <RefreshCw aria-hidden />
            {t('deliveries.redeliver')}
          </Button>
        ),
        size: 130,
      },
    ],
    [format, onRedeliver, redeliveringId, t],
  );
}
